import { Box } from '@chakra-ui/react';
import React from 'react';

import { useCodingScreen } from '../../interface';
import { readState } from '../../slots/readState';
import { renderPlainText } from '../../slots/renderPlainText';
import { FOOTER_H, STRIP_BG, STRIP_BORDER } from '../../styles/tokens';
import { Slot } from '../Slot';

/**
 * RIGHT-pane footer -- reserves space for a review-progress summary
 * (`right.footerSummary.contentId: 'summary.reviewProgress'`); empty by default, no summary
 * data this story. Unlike `TopBarContainer`, `FooterSummaryContainer` (schema.ts)
 * carries no `visible` toggle and no item list -- only its own `contentKey` + `contentId` (a
 * `DataContainerMarker`) --
 * so this molecule is always rendered and has no alternate-config case: a future story fills this
 * container's content from `contentId`, not a rendered item list.
 *
 * CHROME: the off-white strip background/top-border (`STRIP_BG`/
 * `STRIP_BORDER`, styles/tokens.ts) come via ordinary Chakra props, but height stays on a raw
 * `style` object (not Chakra's `h`/`minH`, which compile to an emotion class rather than an inline
 * style) so the pre-existing `.style.height` assertion in `PaneFooterSummary.test.tsx` keeps working
 * unchanged -- mirroring `TopBarContainer`'s same "raw style for the tested dimension, Chakra props
 * for everything else" split.
 *
 * DATUM CONTAINER: the region `<Box>` stays region-only -- `data-content-
 * key` unchanged, NO `data-slot-key` (that attribute is for orderable list ITEMS, not a container's
 * own single bound datum). `footerSummary` (a `DataContainerMarker`) is itself the `Slot` item,
 * bound to its own `contentId` (`'summary.reviewProgress'`); the fallback `renderPlainText(value)`
 * (no `?? label` -- a datum container has no `label` to fall back to) reproduces the original
 * empty body exactly when `value` is `undefined` (the empty-shell default): `renderPlainText
 * (undefined)` is `null`, which renders nothing.
 */
export const PaneFooterSummary: React.FC = () => {
  const { config, state } = useCodingScreen();
  const { mode } = config;
  const { footerSummary } = config.right;
  const value = readState(state, footerSummary.contentId);

  return (
    <Box
      role="region"
      aria-label="Review progress summary"
      data-content-key={footerSummary.contentKey}
      flexShrink={0}
      bg={STRIP_BG}
      borderTop={`1px solid ${STRIP_BORDER}`}
      style={{
        height: FOOTER_H,
        minHeight: FOOTER_H
      }}
    >
      <Slot
        item={footerSummary}
        value={value}
        mode={mode}
        fallback={() => renderPlainText(value)}
      />
    </Box>
  );
};
